import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../../utils/notifyUser";

const ChatHistoryPage = () => {
    const navigate = useNavigate();
    const { founder_id, investor_id } = useParams();
    const [chatHistory, setChatHistory] = useState([]);
    const [checked, setChecked] = useState({});
    const [publishing, setPublishing] = useState(false);

    useEffect(() => {
        fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/chat-history?founder_id=${founder_id}&investor_id=${investor_id}`,
            {
                // Include cookies for authentication
                credentials: "include",
            },
        )
            .then((response) => {
                if(!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                setChatHistory(data);
                // Default: check investor messages, uncheck entrepreneur
                const initialChecked = {};
                data.forEach((msg) => {
                    // If sender is investor, checked; else unchecked
                    initialChecked[msg.id] =
                        msg.sender_role === "investor" ? true : false;
                    // If is_published is present, use that
                    if(typeof msg.is_published !== "undefined")
                        initialChecked[msg.id] = !!msg.is_published;
                });
                setChecked(initialChecked);
            })
            .catch((error) =>
                console.error("Error fetching chat history:", error),);
    }, [founder_id, investor_id]);

    const handleCheckbox = (id) => {
        setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handlePublish = async () => {
        setPublishing(true);
        try {
            const updates = chatHistory.map((msg) => ({
                id: msg.id,
                is_published: checked[msg.id] ? 1 : 0,
            }));
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/publish-messages`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    // Include cookies for authentication
                    credentials: "include",
                    body: JSON.stringify({ updates }),
                },
            );

            if(!res.ok) {
                throw new Error(`HTTP error! Status: ${res.status}`);
            }

            const data = await res.json();
            if(data.success) {
                notifyUser(null, "success", "Publish status updated successfully.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "Failed to update publish status.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(e) {
            console.error("Error updating publish status:", e);
            notifyUser(null, "error", "Error updating publish status.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setPublishing(false);
        }
    };
    // Helper to check if this conversation should have publish controls
    // Only investor-entrepreneur conversations should have publish controls
    const shouldShowPublishControls =
        chatHistory.length > 0 &&
        chatHistory.some((msg) => msg.sender_role === "investor") &&
        chatHistory.some((msg) => msg.sender_role === "entrepreneur") &&
        // Make sure no other roles are present (like admin)
        !chatHistory.some(
            (msg) =>
                msg.sender_role !== "investor" &&
                msg.sender_role !== "entrepreneur",
        );

    // For backward compatibility with existing code
    const isAdminConversation = !shouldShowPublishControls;

    return (
        <div
            style={{
                padding: "2rem",
                maxWidth: "1400px",
                margin: "0 auto",
                background: "#F5F7FA",
                minHeight: "90vh",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "2rem",
                    paddingBottom: "1rem",
                    borderBottom: "1px solid #C2D7E2",
                    position: "relative",
                }}
            >
                <button
                    type="button"
                    aria-label="Back"
                    className="flex items-center gap-2 text-navy hover:text-navy-2 font-semibold text-base px-3 py-2 rounded-lg transition-colors duration-200 border border-[#C2D7E2] bg-[#F5FAFC]"
                    onClick={() => navigate("/chat-logs")}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <i className="fa fa-arrow-left" aria-hidden="true"></i>
                    Back
                </button>
                <h2
                    style={{
                        color: "var(--navy)",
                        letterSpacing: 1.5,
                        fontWeight: 700,
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                        margin: 0,
                    }}
                >
                    Chat History
                </h2>
            </div>
            {/* Publish button - Temporarily hidden but functionality preserved */}
            {!isAdminConversation && (
                <div
                    style={{
                        marginBottom: 24,
                        textAlign: "right",
                        display: "none",
                    }}
                >
                    <button
                        onClick={handlePublish}
                        disabled={publishing}
                        style={{
                            background: "var(--navy)",
                            color: "white",
                            border: "none",
                            borderRadius: 6,
                            padding: "10px 24px",
                            fontWeight: 600,
                            fontSize: 16,
                            cursor: publishing ? "not-allowed" : "pointer",
                            opacity: publishing ? 0.7 : 1,
                        }}
                    >
                        {publishing ? "Publishing..." : "Publish"}
                    </button>
                </div>
            )}
            <div
                style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    padding: "1.5rem",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                    minHeight: 220,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        maxHeight: "70vh",
                        overflowY: "auto",
                        paddingRight: "8px",
                    }}
                >
                    {chatHistory.length === 0 ? (
                        <p
                            style={{
                                textAlign: "center",
                                color: "#666",
                                padding: "2rem 0",
                            }}
                        >
                            No messages found.
                        </p>
                    ) : (
                        chatHistory.map((chat) => {
                            const isSenderA =
                                parseInt(chat.founder_id) ===
                                parseInt(founder_id);
                            const alignment = isSenderA
                                ? "flex-start"
                                : "flex-end";
                            const bgColor = isSenderA ? "var(--navy)" : "#C2D7E2";
                            const textColor = isSenderA ? "white" : "var(--navy)";
                            const border = isSenderA
                                ? "none"
                                : "1.5px solid var(--navy)";
                            return (
                                <div
                                    key={chat.id}
                                    style={{
                                        display: "flex",
                                        justifyContent: alignment,
                                        alignItems: "center",
                                    }}
                                >
                                    {/* Checkboxes - Temporarily hidden but functionality preserved */}
                                    {!isAdminConversation && (
                                        <input
                                            type="checkbox"
                                            checked={!!checked[chat.id]}
                                            onChange={() =>
                                                handleCheckbox(chat.id)
                                            }
                                            style={{
                                                marginRight: 12,
                                                accentColor: "var(--navy)",
                                                width: 18,
                                                height: 18,
                                                display: "none",
                                            }}
                                        />
                                    )}
                                    <div
                                        style={{
                                            backgroundColor: bgColor,
                                            color: textColor,
                                            padding: "12px 16px",
                                            borderRadius: isSenderA
                                                ? "16px 16px 16px 4px"
                                                : "16px 16px 4px 16px",
                                            maxWidth: "80%",
                                            boxShadow:
                                                "0 1px 2px rgba(0,0,0,0.1)",
                                            border: border,
                                        }}
                                    >
                                        <p
                                            style={{
                                                margin: 0,
                                                fontWeight: "600",
                                                color: textColor,
                                            }}
                                        >
                                            {chat.sender}:
                                        </p>
                                        <p
                                            style={{
                                                margin: "8px 0",
                                                color: textColor,
                                                lineHeight: "1.4",
                                            }}
                                        >
                                            {chat.message}
                                        </p>
                                        <small
                                            style={{
                                                fontSize: "0.75rem",
                                                color: isSenderA
                                                    ? "#C2D7E2"
                                                    : "var(--navy)",
                                                display: "block",
                                                textAlign: "right",
                                            }}
                                        >
                                            {new Date(
                                                chat.timestamp,
                                            ).toLocaleString([], {
                                                year: "numeric",
                                                month: "short",
                                                day: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </small>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatHistoryPage;
